import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Modal, Button, Space, Image, message, Typography } from 'antd';

const { Text } = Typography;
import {
  LeftOutlined,
  RightOutlined,
  DeleteOutlined,
  PlusOutlined,
  FieldTimeOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../../../utils/config';
import { getOriginalPoiUrl, poiDosyaAdiUzantili } from '../../../utils/poifotoUtils';
import JSZip from 'jszip';

import { useTheme } from '../../../context/ThemeContext';
import FormikForm from '../../form/FormikForm';

const ImageModal = ({
  open,
  onClose,
  images = [],
  startIndex = 0,
  maptriksId,
  onImagesChange,
  onActiveImageChange,
  selectionMode = false,
  maxSelections = 3,
  onSelectionComplete
}) => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  
  const [modalImageIndex, setModalImageIndex] = useState(startIndex);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [selectedImageIndices, setSelectedImageIndices] = useState([]);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const addFileInputRef = useRef(null);

  useEffect(() => {
    if (open && images.length > 0) {
      try {
        setModalImageIndex(Math.max(0, Math.min(startIndex, images.length - 1)));
      } catch (_) {
        setModalImageIndex(0);
      }
    }
    if (open && selectionMode) {
      setSelectedImageIndices([]);
    }
  }, [open, startIndex, images.length, selectionMode]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!open) return;
      if (images.length <= 1) return;
      
      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          event.stopPropagation();
          setModalImageIndex((prev) => prev > 0 ? prev - 1 : images.length - 1);
          break;
        case 'ArrowRight':
          event.preventDefault();
          event.stopPropagation();
          setModalImageIndex((prev) => (prev + 1) % images.length);
          break;
        case 'Escape':
          event.preventDefault();
          event.stopPropagation();
          onClose();
          break;
        default:
          break;
      }
    };

    if (open) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, images.length, onClose]);

  const handlePrevious = useCallback(() => {
    setModalImageIndex((i) => i > 0 ? i - 1 : images.length - 1);
  }, [images.length]);

  const handleNext = useCallback(() => {
    setModalImageIndex((i) => (i + 1) % (images.length || 1));
  }, [images.length]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleDeleteCurrent = useCallback(async () => {
    if (!maptriksId) {
      message.error('Lokasyon ID bulunamadı');
      return;
    }

    if (!images[modalImageIndex]) {
      message.error('Silinecek resim bulunamadı');
      return;
    }

    try {
      const newImages = images.filter((_, idx) => idx !== modalImageIndex);
      const nextIndex = Math.max(0, Math.min(modalImageIndex - 1, newImages.length - 1));
      const imageIds = newImages.map(img => img.id);
      await axios.put(`${API_BASE_URL}/atm/${maptriksId}`, {
        lokasyonResim: imageIds
      });

      if (onImagesChange) {
        setModalImageIndex(nextIndex);
        onImagesChange(newImages);
        
        if (onActiveImageChange) {
          onActiveImageChange(Math.max(0, nextIndex));
        }
      }

      message.success('Resim listeden kaldırıldı ve veritabanı güncellendi');
    } catch (error) {
      console.error('Resim silme hatası:', error);
      message.error('Resim silinirken hata oluştu');
    }
  }, [modalImageIndex, images, onImagesChange, onActiveImageChange, maptriksId]);

  const handleDownloadAll = useCallback(async () => {
    if (!maptriksId || !images || images.length === 0) {
      message.warning('İndirilecek resim bulunamadı');
      return;
    }

    const selectedImage = images[modalImageIndex];
    if (!selectedImage) {
      message.warning('Seçili resim bulunamadı');
      return;
    }

    try {
      message.loading({ content: 'Büyük resim indiriliyor...', key: 'download' });
      
      // Eski getLargeImageUrl yalnızca '/thumbnail/', '/thumb/', '_small.' gibi
      // kalıpları tanıyordu; buradaki şema ise imgproxy dönüşümü
      // ('.../rs:fill:1100:650/q:70'). Hiçbir kalıp eşleşmediği için indirilen
      // dosya küçültülmüş sürümdü (25.08 md, madde 5). Artık dönüşüm eki
      // kesilerek nesnenin orijinali indiriliyor.
      const getLargeImageUrl = (url) => getOriginalPoiUrl(url);

      try {
        let largeUrl = selectedImage.originalUrl || selectedImage.largeUrl;
        if (!largeUrl && selectedImage.url) {
          largeUrl = getLargeImageUrl(selectedImage.url);
        }
        const urlToFetch = (largeUrl && largeUrl !== selectedImage.url) ? largeUrl : selectedImage.url;
        const response = await fetch(urlToFetch);
        if (!response.ok) {
          console.warn(`Büyük resim yüklenemedi: ${selectedImage.id}, orijinal URL deneniyor...`);
          if (urlToFetch !== selectedImage.url) {
            const fallbackResponse = await fetch(selectedImage.url);
            if (!fallbackResponse.ok) {
              console.warn(`Resim yüklenemedi: ${selectedImage.id}`);
              message.error({ content: 'Resim yüklenemedi', key: 'download', duration: 3 });
              return;
            }
            const blob = await fallbackResponse.blob();
            const fileName = poiDosyaAdiUzantili(
              selectedImage.id || selectedImage.url.split('/').pop() || `image_${modalImageIndex}`,
              blob.type
            );
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            message.success({ content: 'Resim başarıyla indirildi', key: 'download', duration: 3 });
            return;
          }
          console.warn(`Resim yüklenemedi: ${selectedImage.id}`);
          message.error({ content: 'Resim yüklenemedi', key: 'download', duration: 3 });
          return;
        }
        
        const blob = await response.blob();
        // Orijinal nesneler uzantısız ve `application/octet-stream` gelebiliyor;
        // uzantı eklenmezse inen dosya açılmıyor.
        const fileName = poiDosyaAdiUzantili(
          selectedImage.id || urlToFetch.split('/').pop() || `image_${modalImageIndex}`,
          blob.type
        );
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        message.success({ content: 'Resim başarıyla indirildi', key: 'download', duration: 3 });
      } catch (error) {
        console.error(`Resim indirilirken hata: ${selectedImage.id}`, error);
        message.error({ content: 'Resim indirilirken hata oluştu', key: 'download', duration: 3 });
      }
    } catch (error) {
      console.error('İndirme hatası:', error);
      message.error({ content: 'Resim indirilirken hata oluştu', key: 'download', duration: 3 });
    }
  }, [images, maptriksId, modalImageIndex]);

  const handleAddFiles = useCallback((e) => {
    const files = Array.from(e?.target?.files || []);
    if (!files.length) return;
    
    if (onImagesChange) {
      const newItems = files.map(f => ({ id: f.name, url: URL.createObjectURL(f), tarih: '' }));
      const newImages = [...images, ...newItems];
      onImagesChange(newImages);
    }
    
    message.success(`${files.length} resim eklendi (yerel)`);
    try { e.target.value = null; } catch (_) { }
  }, [images, onImagesChange]);

  const handleOpenFormModal = useCallback(() => {
    setFormModalOpen(true);
  }, []);

  const handleFormSuccess = useCallback((result) => {
    message.success('Resim bilgileri başarıyla kaydedildi');
    setFormModalOpen(false);
  }, [onImagesChange]);

  const handleThumbnailClick = useCallback((idx) => {
    if (selectionMode) {
      setSelectedImageIndices(prev => {
        const isSelected = prev.includes(idx);
        if (isSelected) {
          return prev.filter(i => i !== idx);
        } else {
          if (prev.length < maxSelections) {
            return [...prev, idx];
          } else {
            return [...prev.slice(1), idx];
          }
        }
      });
    } else {
      setModalImageIndex(idx);
    }
  }, [selectionMode, maxSelections]);

  const handleImagesReorder = useCallback(async (newImages, newActiveIndex) => {
    if (!maptriksId) {
      message.error('Lokasyon ID bulunamadı');
      return;
    }

    try {
      const imageIds = newImages.map(img => img.id);
      await axios.put(`${API_BASE_URL}/atm/${maptriksId}`, {
        lokasyonResim: imageIds
      });

      if (onImagesChange) {
        onImagesChange(newImages);
        
        if (onActiveImageChange && newActiveIndex !== undefined) {
          onActiveImageChange(newActiveIndex);
        }
      }
    } catch (error) {
      console.error('Sıralama kaydetme hatası:', error);
      message.error('Sıralama kaydedilirken hata oluştu');
    }
  }, [maptriksId, onImagesChange, onActiveImageChange]);

  const handleDragStart = useCallback((e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target.outerHTML);
    e.target.style.opacity = '0.5';
  }, []);

  const handleDragOver = useCallback((e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index);
    }
  }, [draggedIndex]);

  const handleDragLeave = useCallback(() => {
    setDragOverIndex(null);
  }, []);

  const handleDrop = useCallback((e, dropIndex) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const newImages = [...images];
    const draggedImage = newImages[draggedIndex];
    newImages.splice(draggedIndex, 1);
    newImages.splice(dropIndex, 0, draggedImage);
    let newActiveIndex = modalImageIndex;
    if (modalImageIndex === draggedIndex) {
      newActiveIndex = dropIndex;
    } else if (draggedIndex < modalImageIndex && dropIndex >= modalImageIndex) {
      newActiveIndex = modalImageIndex - 1;
    } else if (draggedIndex > modalImageIndex && dropIndex <= modalImageIndex) {
      newActiveIndex = modalImageIndex + 1;
    }

    setDraggedIndex(null);
    setDragOverIndex(null);
    setModalImageIndex(newActiveIndex);
    if (onImagesChange) {
      onImagesChange(newImages);
    }

    handleImagesReorder(newImages, newActiveIndex);
  }, [draggedIndex, images, modalImageIndex, onImagesChange, handleImagesReorder]);

  const handleDragEnd = useCallback((e) => {
    e.target.style.opacity = '';
    setDraggedIndex(null);
    setDragOverIndex(null);
  }, []);

  if (!open) return null;

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      width="min(1400px, calc(100vw - 32px))"
      footer={null}
      styles={{ body: { padding: 12, background: darkMode ? '#1b1d22' : '#fff' } }}
    >
      <div style={{ display: 'flex', gap: 12, alignItems: 'stretch' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            borderRadius: 8,
            overflow: 'hidden',
            position: 'relative',
            height: 'min(640px, 62vh)',
            background: '#0f1115',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {images[modalImageIndex] ? (
              <Image
                src={images[modalImageIndex].url}
                alt={images[modalImageIndex].id}
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            ) : (
              <div style={{ color: '#999' }}>Resim yok</div>
            )}
            {images[modalImageIndex]?.tarih && (
              <div style={{
                position: 'absolute',
                top: 8,
                right: 8,
                background: 'rgba(0,0,0,0.7)',
                color: 'white',
                padding: '4px 8px',
                borderRadius: 4,
                fontSize: 12,
                zIndex: 1
              }}>
                {images[modalImageIndex].tarih}
              </div>
            )}
            
            {images.length > 1 && (
              <>
                <Button
                  shape="circle"
                  icon={<LeftOutlined />}
                  size="large"
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: 16,
                    transform: 'translateY(-50%)',
                    zIndex: 2,
                    background: 'rgba(0,0,0,0.6)',
                    color: 'white',
                    border: 'none',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                    opacity: 0.8,
                    transition: 'opacity 0.2s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.opacity = '1'}
                  onMouseLeave={(e) => e.target.style.opacity = '0.8'}
                  onClick={handlePrevious}
                />
                <Button
                  shape="circle"
                  icon={<RightOutlined />}
                  size="large"
                  style={{
                    position: 'absolute',
                    top: '50%',
                    right: 16,
                    transform: 'translateY(-50%)',
                    zIndex: 2,
                    background: 'rgba(0,0,0,0.6)',
                    color: 'white',
                    border: 'none',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                    opacity: 0.8,
                    transition: 'opacity 0.2s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.opacity = '1'}
                  onMouseLeave={(e) => e.target.style.opacity = '0.8'}
                  onClick={handleNext}
                />
              </>
            )}
            
            {images.length > 1 && (
              <div style={{
                position: 'absolute',
                bottom: 16,
                left: '50%',
                transform: 'translateX(-50%)',
                background: 'rgba(0,0,0,0.7)',
                color: 'white',
                padding: '4px 12px',
                borderRadius: 16,
                fontSize: 12,
                zIndex: 1
              }}>
                {modalImageIndex + 1} / {images.length}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
            <Space>
              {images.length > 1 && (
                <>
                  <Button onClick={handlePrevious} disabled={images.length <= 1}>
                    <LeftOutlined /> Önceki
                  </Button>
                  <Button onClick={handleNext} disabled={images.length <= 1}>
                    Sonraki <RightOutlined />
                  </Button>
                </>
              )}
            </Space>
            <Space>
              <Button icon={<DownloadOutlined />} onClick={handleDownloadAll}>
                İndir
              </Button>
              <Button danger icon={<DeleteOutlined />} onClick={handleDeleteCurrent}>
                Sil
              </Button>
              <Button
                size="small"
                style={{ minWidth: 56, height: 40, borderRadius: 4, fontSize: 12 }}
                onClick={() => navigate(`/arsiv/${maptriksId}`)}
              >
                <FieldTimeOutlined />
              </Button>
            </Space>
          </div>
        </div>
        <div style={{ width: 220 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <b style={{ color: darkMode ? '#ddd' : '#333' }}>Tüm Resimler</b>
            <span style={{ color: '#999', fontSize: 12 }}>{images.length}</span>
          </div>
          <div style={{ maxHeight: 460, overflowY: 'auto', paddingRight: 4 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {images.map((img, idx) => {
                const isSelected = selectedImageIndices.includes(idx);
                const isActive = idx === modalImageIndex;
                const isDragging = draggedIndex === idx;
                const isDragOver = dragOverIndex === idx;
                return (
                  <div
                    key={img.id + '_' + idx}
                    draggable={!selectionMode}
                    onDragStart={(e) => !selectionMode && handleDragStart(e, idx)}
                    onDragOver={(e) => !selectionMode && handleDragOver(e, idx)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => !selectionMode && handleDrop(e, idx)}
                    onDragEnd={handleDragEnd}
                    onClick={() => handleThumbnailClick(idx)}
                    style={{
                      border: isSelected 
                        ? '2px solid #52c41a' 
                        : isActive 
                          ? '2px solid #1677ff' 
                          : isDragOver
                            ? '2px solid #1677ff'
                            : '1px solid #2a2d35',
                      borderRadius: 6,
                      cursor: selectionMode ? 'pointer' : 'grab',
                      overflow: 'hidden',
                      position: 'relative',
                      background: darkMode ? '#111' : '#fafafa',
                      opacity: isDragging ? 0.5 : (isSelected ? 1 : 0.8),
                      transform: isDragOver ? 'scale(1.05)' : 'scale(1)',
                      transition: isDragging ? 'none' : 'transform 0.2s ease, opacity 0.2s ease',
                      zIndex: isDragging ? 1000 : (isDragOver ? 999 : 1)
                    }}
                  >
                    <img 
                      src={img.url} 
                      style={{ width: '100%', height: 72, objectFit: 'cover', display: 'block', pointerEvents: 'none' }} 
                      alt={img.id}
                      draggable={false}
                    />
                    {isSelected && (
                      <div style={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        background: '#52c41a',
                        borderRadius: '50%',
                        width: 20,
                        height: 20,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: 12,
                        fontWeight: 'bold',
                        pointerEvents: 'none'
                      }}>
                        {selectedImageIndices.indexOf(idx) + 1}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          <div style={{ marginTop: 8, display: 'flex', gap: 8, flexDirection: 'column' }}>
            {selectionMode && (
              <div style={{ marginBottom: 8 }}>
                <Text style={{ color: darkMode ? '#ddd' : '#333', fontSize: 12 }}>
                  {selectedImageIndices.length} / {maxSelections} resim seçildi
                </Text>
                {selectedImageIndices.length > 0 && (
                  <Button
                    type="primary"
                    block
                    style={{ marginTop: 8 }}
                    onClick={() => {
                      const selectedImages = selectedImageIndices.map(idx => images[idx]).filter(Boolean);
                      if (onSelectionComplete) {
                        onSelectionComplete(selectedImages);
                      }
                      if (onImagesChange) {
                        onImagesChange(selectedImages);
                      }
                      onClose();
                    }}
                  >
                    Seçilen Resimleri Uygula ({selectedImageIndices.length})
                  </Button>
                )}
              </div>
            )}
            <input 
              ref={addFileInputRef} 
              type="file" 
              accept="image/*" 
              multiple 
              style={{ display: 'none' }} 
              onChange={handleAddFiles} 
            />
            <Button 
              type="primary" 
              block 
              icon={<PlusOutlined />} 
              onClick={handleOpenFormModal}
            >
              Yeni Resim Ekle
            </Button>
          </div>
        </div>
      </div>

      <Modal
        open={formModalOpen}
        onCancel={() => setFormModalOpen(false)}
        title="Yeni Resim Ekle"
        width={600}
        footer={null}
        destroyOnHidden
        styles={{ body: { padding: 24, background: darkMode ? '#1b1d22' : '#fff' } }}
      >
        <FormikForm
          formId={66}
          function="Update"
          uniqueValue={maptriksId}
          uniqueField="MaptriksID"
          tableName="maptriks_lokasyonlar"
          onSuccess={handleFormSuccess}
        />
      </Modal>
    </Modal>
  );
};

export default ImageModal;

